import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getLatestPerformanceSummary,
  generatePerformanceSummary,
  getPerformanceData
} from '../components/apiClient';
import { useJWTAuth } from '../components/JWTAuthContext';
import { PerformanceHeader, QuickStatsCards, PerformanceTrends, DifficultyAnalysis, AIAnalysisSection, AnalysisPeriodInfo, NoDataAvailable, PerformanceSummary, PerformanceData } from '../components/performance';

const Performance: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useJWTAuth();

  const [performanceSummary, setPerformanceSummary] = useState<PerformanceSummary | null>(null);
  const [performanceData, setPerformanceData] = useState<PerformanceData | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastGenerated, setLastGenerated] = useState<string | null>(null);

  const fetchPerformanceData = React.useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      setError(null);

      // Fetch latest performance summary
      const summaryResponse = await getLatestPerformanceSummary(user.id);

      if (summaryResponse && summaryResponse.status === 'success' && summaryResponse.result) {
        setPerformanceSummary(summaryResponse.result);
        setLastGenerated(summaryResponse.result.created_at);
      } else {
        setPerformanceSummary(null);
        setLastGenerated(null);
      }

      // Fetch performance analytics data
      try {
        const analyticsResponse = await getPerformanceData(user.id);
        if (analyticsResponse && analyticsResponse.status === 'success') {
          setPerformanceData(analyticsResponse.result);
        }
      } catch (analyticsError) {
        // Performance data is optional, don't fail the whole operation
        console.warn('Performance analytics data not available:', analyticsError);
      }

    } catch (error) {
      console.error('Error fetching performance data:', error);
      setError('Failed to load performance data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      if (user === null) {
        // Still checking auth status
        return;
      }
      // Not authenticated, redirect to login
      navigate('/login');
      return;
    }

    fetchPerformanceData();
  }, [user, navigate, fetchPerformanceData]);

  const generatePerformanceAnalysis = async (forceRegenerate = false) => {
    try {
      setIsGenerating(true);
      setError(null);

      const response = await generatePerformanceSummary(user.id, forceRegenerate, 6);

      if (response && response.status === 'success') {
        setPerformanceSummary(response.result);
        setLastGenerated(response.result.created_at);

        // Refresh performance data
        await fetchPerformanceData();

        alert('Performance analysis generated successfully!');
      } else {
        throw new Error(response?.message || 'Failed to generate analysis');
      }

    } catch (error) {
      console.error('Error generating performance analysis:', error);
      setError('Failed to generate performance analysis. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading || user === null) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">
                  {user === null ? 'Checking authentication...' : 'Loading your performance analysis...'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Header Section */}
      <PerformanceHeader 
        isGenerating={isGenerating}
        lastGenerated={lastGenerated}
        error={error}
        onGenerateAnalysis={generatePerformanceAnalysis}
        formatDate={formatDate}
      />

      {!performanceSummary ? (
        <NoDataAvailable 
          isGenerating={isGenerating}
          onGenerateAnalysis={() => generatePerformanceAnalysis(false)}
        />
      ) : (
        <>
          {/* Analysis Period Info */}
          <AnalysisPeriodInfo
            analysisPeriodStart={formatDate(performanceSummary.summary_period_start)}
            analysisPeriodEnd={formatDate(performanceSummary.summary_period_end)}
            generationDate={formatDate(performanceSummary.generation_date)}
          />

          {/* Quick Stats Cards */}
          <QuickStatsCards performanceSummary={performanceSummary} />

          {/* Performance Trends */}
          <PerformanceTrends performanceSummary={performanceSummary} />

          {/* Difficulty Analysis */}
          <DifficultyAnalysis performanceSummary={performanceSummary} />

          {/* AI Analysis Section */}
          <AIAnalysisSection performanceSummary={performanceSummary} />
        </>
      )}
    </div>
  );
};

export default Performance;
